import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, Card, Row, Col, message, Tooltip, Popconfirm, Input, Tag, Select,
    Dropdown, Modal
} from 'antd';
import {
    EditOutlined, LockOutlined, UnlockOutlined, DeleteOutlined, AppleOutlined, WindowsOutlined, AndroidOutlined, CloudTwoTone,
    MessageOutlined, ThunderboltOutlined, EnvironmentOutlined, SettingOutlined, UploadOutlined, DownloadOutlined,
    InboxOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import DeviceEditFormAdmin from './DeviceEditFormAdmin';
import SendMessagePopup from '../DeviceConfigs/SendMessagePopup';
import { useTheme } from '../../context/ThemeContext';
import { useScreenSize } from '../../context/ScreenSizeContext'; 
import '../../styles/DeviceList.css';
import ModalBatteryHistory from '../Devices/ModalBatteryHistory';
import ModalLocationInfo from '../Devices/ModalLocationInfo';
import { exportSelectedDevices, exportAllDevices, } from './DeviceList/exportHelpers';
import ImportDevicesModal from './DeviceList/ImportDevicesModal';
import UpdateBulkClient from './DeviceList/updateBulkClient';

const { Search } = Input; 

const DeviceListAdmin = () => {
  const { darkTheme } = useTheme();
  // Hook-based modal so the confirm dialog follows the app theme (dark/light)
  const [modal, modalContextHolder] = Modal.useModal();
  const [devices, setDevices] = useState([]);
  const [deviceCounts, setDeviceCounts] = useState({
    iOS: 0,
    Android: 0,
    WinUI: 0, 
  });
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [editingDevice, setEditingDevice] = useState(null); 
  const [searchText, setSearchText] = useState('');
  const [foundDevices, setFoundDevices] = useState([]);
  const { isMobile } = useScreenSize();
  const [sendMessagePopupRecord, setSendMessagePopupRecord] = useState(null);
  const [batteryModalVisible, setBatteryModalVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [selectedDeviceLocation, setSelectedDeviceLocation] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);

  // rows currently visible after Table's own filters/sorters
  const [tableFilters, setTableFilters] = useState({});

    // selection + export/import state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  //Update bulk clientID
  const [clients, setClients] = useState([]);
  const [bulkClientModalVisible, setBulkClientModalVisible] = useState(false);
  const [bulkClientLoading, setBulkClientLoading] = useState(false);
  const [bulkClientId, setBulkClientId] = useState(undefined);

  // rowSelection to enable checkboxes in table
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    preserveSelectedRowKeys: true,
  };

  const getUniqueClients = () => {
    const values = devices
      .map(item => item.clientID?.name)
      .filter(Boolean);

    return [...new Set(values)].map(value => ({
        text: value,
        value: value,
    }));
  };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [devicesRes, clientsRes] = await Promise.all([
                    axios.get('/devices/admin'),
                    axios.get('/clients'),
                ]);

                const data = devicesRes.data;
                setDevices(data);
                setClients(clientsRes.data);

                // Extracting unique events from device configurations
                const uniqueEvents = [];
                const seenEventIds = new Set();

                data.forEach(device => {
                    const event = device.DeviceConfigId?.SelectedEventId;
                    if (event && event._id && !seenEventIds.has(event._id)) {
                        seenEventIds.add(event._id);
                        uniqueEvents.push({
                            _id: event._id,
                            mnemonic: event.mnemonic
                        });
                    }
                });
                setEvents(uniqueEvents);

                // Count devices per platform
                const counts = data.reduce((acc, device) => {
                    const platform = device.Platform;
                    if (platform === 'MacCatalyst') {
                        acc.iOS = (acc.iOS || 0) + 1;
                    } else {
                        acc[platform] = (acc[platform] || 0) + 1;
                    }
                    return acc;
                }, {});

                setDeviceCounts({
                    iOS: counts.iOS || 0,
                    Android: counts.Android || 0,
                    WinUI: counts.WinUI || 0,
                });
            } catch (error) {
                console.error('An error occurred whilst retrieving the initial data:', error);
                message.error('Error loading initial data');
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        const lowerCaseSearchText = searchText.toLowerCase();

        const filtered = devices.filter(device => {
            const matchesSearch =
                device.Name.toLowerCase().includes(lowerCaseSearchText) ||
                (device.DeviceConfigId?.DeviceConfigName || '').toLowerCase().includes(lowerCaseSearchText) ||
                device.SerialNumber.toLowerCase().includes(lowerCaseSearchText) ||
                device.Platform.toLowerCase().includes(lowerCaseSearchText);

            const matchesEvent = !selectedEventId || device.DeviceConfigId?.SelectedEventId?._id === selectedEventId;

            return matchesSearch && matchesEvent;
        });

        setFoundDevices(filtered);

        // We also updated the platform count based on the filtered result.
        const counts = filtered.reduce((acc, device) => {
            const platform = device.Platform === 'MacCatalyst' ? 'iOS' : device.Platform;
            acc[platform] = (acc[platform] || 0) + 1;
            return acc;
        }, {});

        setDeviceCounts({
            iOS: counts.iOS || 0,
            Android: counts.Android || 0,
            WinUI: counts.WinUI || 0,
        });
    }, [searchText, devices, selectedEventId]);

  useEffect(() => {
    const fetchUpdatedDevices = async () => {
      try {
        const response = await axios.get('/devices/admin');
        setDevices(response.data);
      } catch (error) {
        message.error('Error fetching updated devices');
        console.error('Error fetching updated devices:', error);
      }
    };
  
    // We call the function to update the devices every 10 seconds, for example.
    const intervalId = setInterval(fetchUpdatedDevices, 10000);
  
    // We clean the interval when the component is disassembled
    return () => clearInterval(intervalId);
  }, []);

  // Map platform names to corresponding icons for Table
  const platformIcons = {
    iOS: <AppleOutlined style={{ fontSize: '24px', color: darkTheme ? '#FFFFFF' : '#000000' }} />,
    Android: <AndroidOutlined style={{ fontSize: '24px', color: darkTheme ? '#A4C639' : '#3DDC84' }} />,
    WinUI: <WindowsOutlined style={{ fontSize: '24px', color: darkTheme ? '#FFFFFF' : '#0078D6' }} />,
    MacCatalyst: <AppleOutlined style={{ fontSize: '24px', color: darkTheme ? '#FFFFFF' : '#000000' }} />,
  };

  // Manages platform selection
  const handlePlatformClick = (platform) => {
    setSelectedPlatforms(prev => {
      const updated = new Set(prev);
      if (updated.has(platform)) {
        updated.delete(platform);
      } else {
        updated.add(platform);
      }
      return updated;
    });
  };

    const handleBulkChangeClient = async () => {
        try {
            if (!selectedRowKeys.length) {
                message.warning('Please select at least one device.');
                return;
            }

            if (!bulkClientId) {
                message.warning('Please select a client.');
                return;
            }

            setBulkClientLoading(true);

            await axios.put('/devices/bulk/client', {
                deviceIds: selectedRowKeys,
                clientID: bulkClientId,
            });

            message.success(`Client updated for ${selectedRowKeys.length} device(s).`);

            const refreshed = await axios.get('/devices/admin');
            setDevices(refreshed.data);

            setSelectedRowKeys([]);
            setBulkClientId(undefined);
            setBulkClientModalVisible(false);
        } catch (error) {
            console.error('Error updating devices client in bulk:', error);
            message.error('Error updating client for selected devices.');
        } finally {
            setBulkClientLoading(false);
        }
    };

  // Filter devices according to selected platforms
  const filteredDevices = foundDevices.filter(device => 
    selectedPlatforms.size === 0 || selectedPlatforms.has(device.Platform) || (device.Platform === 'MacCatalyst' && selectedPlatforms.has('iOS'))
  );

  const onlineCount = React.useMemo(
    () => filteredDevices.filter(d => d.wsActive).length,
    [filteredDevices]
  );

  // Define the columns of the table
  const columns = [
    {
      title: (
        <span>
          <CloudTwoTone />
        </span>
      ),
      dataIndex: 'wsActive',
      key: 'wsActive',
      sorter: (a, b) => {
            return (a.wsActive === b.wsActive) ? 0 : a.wsActive ? -1 : 1;
      },
      render: (wsActive) => (
        <span style={{ position: 'relative', display: 'inline-block', width: '15px', height: '15px' }}>
          {/* Background circle (lighter outline) */}
          <span
              style={{
                width: '15px',
                height: '15px',
                borderRadius: '50%',
                backgroundColor: wsActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                position: 'absolute',
                top: 0,
                left: 0
              }}
          />

          {/* Front circle (active or inactive indicator) */}
          <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: wsActive ? '#10B981' : '#EF4444',
                position: 'absolute',
                top: '2.5px',
                left: '2.5px'
              }}
          />
        </span>
      ),
    },
      {
          title: 'Name',
          dataIndex: 'Name',
          key: 'Name',
          sorter: (a, b) => a.Name.localeCompare(b.Name),
          render: (text, record) => (
              <Tooltip title={record.SerialNumber}>
                  <span>{text}</span>
              </Tooltip>
          ),
      },
    {
      title: 'Client',
      dataIndex: ['clientID', 'name'],
      key: 'clientID.name',
      filters: getUniqueClients(),
      onFilter: (value, record) =>
          record.clientID?.name?.toLowerCase().includes(value.toLowerCase()),
      filterSearch: true,
      render: (text, record) => (
          record.clientID?.name ? (
              <Tag color={record.clientID?.clientColor || 'default'}>
                  {record.clientID.name}
              </Tag>
          ) : (
              <Tag color="red">N/A</Tag>
          )
      ),
      sorter: (a, b) => {
          const nameA = a.clientID?.name?.toLowerCase() || '';
          const nameB = b.clientID?.name?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Device Config Name',
      dataIndex: ['DeviceConfigId', 'DeviceConfigName'], 
      key: 'DeviceConfigName',
      responsive: ['md', 'lg'],
      sorter: (a, b) => {
        const configNameA = a.DeviceConfigId?.DeviceConfigName || '';
        const configNameB = b.DeviceConfigId?.DeviceConfigName || '';
        return configNameA.localeCompare(configNameB);
      },
      render: (text) => (
        <Tag color={text ? 'blue' : 'red'}>
          {text || 'Not Assigned'}
        </Tag>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'Platform',
      key: 'Platform',
      render: (platform) => (
          <span>{platformIcons[platform] || 'Unknown'}</span>
      ),
    },
    {
      title: 'App Version',
      dataIndex: 'appVersion',
      key: 'appVersion',
      sorter: (a, b) => (a.appVersion || '').localeCompare(b.appVersion || ''),
      render: (version) => (
          <Tag color={(!version || version === 'unknown') ? 'red' : 'blue'}>
              {version || 'unknown'}
          </Tag>
      ),
    },
    {
      title: 'LC Version',
      dataIndex: 'appLCVersion',
        key: 'appLCVersion',
        sorter: (a, b) => (a.appLCVersion || '').localeCompare(b.appLCVersion || ''),
        render: (version) => (
            <Tag color={(!version || version === 'unknown') ? 'red' : 'blue'}>
                {version || 'unknown'}
            </Tag>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
          const level = record.batteryStatus?.level;
          const state = record.batteryStatus?.state;
          const showBattery = level != null && record.wsActive;
          const showLocation = record.locationStatus &&
              record.locationStatus.latitude &&
              record.locationStatus.longitude;

          const batteryColor =
              level >= 50 ? 'green' : level >= 20 ? 'orange' : 'red';

          return (
              <Space>
                  {/* Invisible placeholders keep every slot occupied, so the
                      status icons line up across rows like in a grid */}
                  {showBattery ? (
                      <Tooltip title={`${state || 'Unknown'} (${level}%)`}>
                          <Tag color={batteryColor} style={{ minWidth: 48, textAlign: 'center' }}>
                              {level}%{' '}
                          </Tag>
                      </Tooltip>
                  ) : (
                      <Tag style={{ minWidth: 48, visibility: 'hidden' }}>0%</Tag>
                  )}

                  {level != null ? (
                      <Tooltip title="Battery Info">
                          <Button
                              type="text"
                              size="small"
                              icon={<ThunderboltOutlined style={{ color: '#FACC15' }} />}
                              onClick={() => {
                                  setSelectedDevice(record);
                                  setBatteryModalVisible(true);
                              }}
                          />
                      </Tooltip>
                  ) : (
                      <Button type="text" size="small" style={{ visibility: 'hidden' }} icon={<ThunderboltOutlined />} />
                  )}

                  {showLocation ? (
                      <Tooltip title="Last location">
                          <Button
                              type="text"
                              size="small"
                              icon={<EnvironmentOutlined style={{ color: darkTheme ? '#69B1FF' : '#1677ff' }} />}
                              onClick={() => {
                                  setSelectedDeviceLocation(record);
                                  setLocationModalVisible(true);
                              }}
                          />
                      </Tooltip>
                  ) : (
                      <Button type="text" size="small" style={{ visibility: 'hidden' }} icon={<EnvironmentOutlined />} />
                  )}
              </Space>
          );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          {record.wsActive ? (
            <Tooltip title="Send message" color="green">
                <Button
                    icon={<MessageOutlined style={{ color: 'green' }} />}
                    onClick={() => handleSendMessage(record)}
                />
            </Tooltip>
          ) : (
            // Invisible placeholder so the action buttons line up across rows
            <Button style={{ visibility: 'hidden' }} icon={<MessageOutlined />} />
          )}

          <Tooltip title="Edit" color="blue">
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={record.isActive ? "Locked" : "Unlocked"} color={record.isActive ? "orange" : "red"}>
            <Button 
              icon={record.isActive ? <UnlockOutlined /> : <LockOutlined style={{ color: 'red' }} />}
              onClick={() => handleLock(record)} 
            />
          </Tooltip>
          <Popconfirm
            title={record.isDeleted ? "Are you sure you want to restore this device?" : "Are you sure you want to archive this device?"}
            onConfirm={() => handleDeleteAdmin(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title={record.isDeleted ? "Restore" : "Archive"} color="orange">
              <Button icon={record.isDeleted ? <InboxOutlined style={{ color: 'red' }}  /> : <InboxOutlined />} />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Delete permanently" color="red">
            <Button danger icon={<DeleteOutlined />} onClick={() => confirmHardDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // compute the actually visible rows after AntD column filters
  const visibleRows = React.useMemo(() => {
        let data = filteredDevices;

        // function to get column key used by filters
        const getColKey = (c) =>
            c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : c.dataIndex);

        (Object.entries(tableFilters || {})).forEach(([filterKey, values]) => {
            if (!values || !values.length) return;
            const col = columns.find((c) => getColKey(c) === filterKey);
            if (!col || typeof col.onFilter !== 'function') return;

            data = data.filter((row) => values.some((v) => col.onFilter(v, row)));
        });

        return data;
  }, [filteredDevices, tableFilters, columns]);

  const handleEdit = (record) => {
    setEditingDevice(record); 
  };
  const handleModalClose = () => {
    setEditingDevice(null); 
  };

  const handleLock = async (record) => {
    try {
      const newActiveState = !record.isActive;
  
      // Defines the values to be updated
      const updatedFields = {
        isActive: newActiveState,
        DeviceConfigId: newActiveState ? record.DeviceConfigId : null,
        DeviceConfigName: newActiveState ? record.DeviceConfigName : null, 
      };
  
      await axios.put(`/devices/${record._id}`, updatedFields);
  
      // Update local status after request
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device._id === record._id 
            ? { ...device, ...updatedFields } 
            : device
        )
      );
      message.success(`Device ${record.Name} has been ${newActiveState ? 'unlocked' : 'locked'} successfully.`);
    } catch (error) {
      message.error('Error updating device active state. Please try again');
    }
  };  

  const handleDeleteAdmin = async (record) => {
    try {
      const newDeleteState = !record.isDeleted;
  
      // Defines the values to be updated
      const updatedFields = {
        isDeleted: newDeleteState,
        DeviceConfigId: newDeleteState ? record.DeviceConfigId : null,
        DeviceConfigName: newDeleteState ? record.DeviceConfigName : null, 
      };
  
      await axios.put(`/devices/${record._id}`, updatedFields);
  
      // Update local status after request
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device._id === record._id 
            ? { ...device, ...updatedFields } 
            : device
        )
      );
      message.success(`Device ${record.Name} has been ${newDeleteState ? 'archived' : 'restored'} successfully.`);
    } catch (error) {
      message.error('Error updating device archived state. Please try again');
    }
  };

  // Asks for confirmation before removing a device from the database for good
  const confirmHardDelete = (record) => {
    modal.confirm({
      title: 'Delete device permanently?',
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      content: `The device "${record.Name}" will be permanently removed from the database. This cannot be undone.`,
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      centered: true,
      styles: { content: { backgroundColor: darkTheme ? '#2a1215' : '#fff1f0' } },
      onOk: () => handleHardDelete(record),
    });
  };

  const handleHardDelete = async (record) => {
    try {
      await axios.delete(`/devices/admin/${record._id}`);
      setDevices(prevDevices => prevDevices.filter(device => device._id !== record._id));
      message.success(`Device ${record.Name} has been permanently deleted.`);
    } catch (error) {
      message.error('Error deleting device permanently. Please try again');
    }
  };

  const handleSendMessage = (record) => {
    setSendMessagePopupRecord({ ...record, type: 'device' });
  };

  // ✅ helper: resolve the column by the filter key emitted by AntD
  const getColumnByFilterKey = (cols, filterKey) => {
    // Build a map from possible keys -> column
    const map = new Map(
        cols.map((c) => {
            const k =
                c.key ??
                (Array.isArray(c.dataIndex)
                    ? c.dataIndex.join('.')               // ['clientID','name'] -> 'clientID.name'
                    : c.dataIndex);
            return [k, c];
        })
    );
    return map.get(filterKey);
  };

  return (
    <div style={{ padding: '24px' }}>
      {modalContextHolder}
      <h1 style={{ marginBottom: '24px' }}>Devices List</h1>
      {/* Platform cards */}
      <Row gutter={16} style={{ marginBottom: '48px' }}> 
        <Col span={8}>
          <Card 
            bordered={false} 
            className={`platform-card ${darkTheme ? 'platform-card-dark' : 'platform-card-light'} ${selectedPlatforms.has('iOS') ? 'selected' : ''}`}
            onClick={() => handlePlatformClick('iOS')}
          >
            <div className="platform-card-content">
              <AppleOutlined 
                className="platform-icon" 
                style={{ 
                  fontSize: '48px', 
                  color: darkTheme ? '#FFFFFF' : '#000000',
                  marginBottom: '15px'
                }} 
              />
              {!isMobile ? <Card.Meta title="Apple" /> : null}
              <Card.Meta 
                description={isMobile ? String(deviceCounts.iOS || 0) : `Devices: ${deviceCounts.iOS || 0}`} 
                style={{ marginTop: '10px' }} 
              />
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card 
            bordered={false} 
            className={`platform-card ${darkTheme ? 'platform-card-dark' : 'platform-card-light'} ${selectedPlatforms.has('WinUI') ? 'selected' : ''}`}
            onClick={() => handlePlatformClick('WinUI')}
          >
            <div className="platform-card-content">
              <WindowsOutlined 
                className="platform-icon" 
                style={{ 
                  fontSize: '48px', 
                  color: '#0078D6', 
                  marginBottom: '15px'
                }} 
              />
              {!isMobile ? <Card.Meta title="Windows" /> : null}
              <Card.Meta 
                description={isMobile ? String(deviceCounts.WinUI || 0) : `Devices: ${deviceCounts.WinUI || 0}`} 
                style={{ marginTop: '10px' }} 
              />
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card 
            bordered={false} 
            className={`platform-card ${darkTheme ? 'platform-card-dark' : 'platform-card-light'} ${selectedPlatforms.has('Android') ? 'selected' : ''}`}
            onClick={() => handlePlatformClick('Android')}
          >
            <div className="platform-card-content">
              <AndroidOutlined 
                className="platform-icon" 
                style={{ 
                  fontSize: '48px', 
                  color: darkTheme ? '#A4C639' : '#3DDC84',
                  marginBottom: '15px'
                }} 
              />
              {!isMobile ? <Card.Meta title="Android" /> : null}
              <Card.Meta 
                description={isMobile ? String(deviceCounts.Android || 0) : `Devices: ${deviceCounts.Android || 0}`} 
                style={{ marginTop: '10px' }} 
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Event Select + Search + Counters + Settings button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        {/* filters */}
        <div style={{ display: 'flex', gap: 16 }}>
            <Select
                allowClear
                placeholder="Filter by event mnemonic"
                style={{ width: 300 }}
                value={selectedEventId || undefined}
                onChange={(value) => setSelectedEventId(value)}
                options={events.map(event => ({
                    label: event.mnemonic,
                    value: event._id
                }))}
            />
            <Search
                placeholder="Search devices..."
                onSearch={value => setSearchText(value)}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 300 }}
            />
            <Dropdown
                trigger={['click']}
                menu={{
                    items: [
                        { key: 'changeClient', label: 'Change client (selected)', icon: <SettingOutlined />, disabled: !selectedRowKeys.length },
                        { type: 'divider' },
                        { key: 'exportSelected', label: 'Export selected', icon: <DownloadOutlined /> },
                        { key: 'exportAll', label: 'Export all (filtered)', icon: <DownloadOutlined /> },
                        { type: 'divider' },
                        { key: 'import', label: 'Import...', icon: <UploadOutlined /> },
                    ],
                    onClick: async ({ key }) => {
                        if (key === 'changeClient') {
                            if (!selectedRowKeys.length) {
                                message.warning('Please select at least one device.');
                                return;
                            }
                            setBulkClientModalVisible(true);
                        }

                        if (key === 'exportSelected') {
                            exportSelectedDevices({
                                devices,
                                selectedRowKeys,
                                messageApi: message,
                                setExportLoading,
                            });
                        }
                        if (key === 'exportAll') {
                            exportAllDevices({
                                filteredDevices: visibleRows,
                                messageApi: message,
                                setExportLoading,
                            });
                        }
                        if (key === 'import') {
                            setImportModalVisible(true);
                        }
                    },
                }}
            >
                <Button icon={<SettingOutlined />} type="default" loading={exportLoading}>
                    Settings
                </Button>
            </Dropdown>
        </div>

        {/* count */}
        <Space>
            <Tag color="green">Online: {onlineCount}</Tag>
            <Tag color="blue">Total: {filteredDevices.length}</Tag>
        </Space>
      </div>

      {/* Table of devices */}
      <Table
        columns={columns}
        dataSource={filteredDevices}
        rowKey="_id"
        scroll={{ x: 'max-content' }}
        rowSelection={rowSelection}
        onChange={(pagination, filters /*, sorter, extra */) => {
            setTableFilters(filters || {});
        }}
      />

      {/* Editing mode */}
      {editingDevice && (
        <DeviceEditFormAdmin
          editingDevice={editingDevice}
          onClose={handleModalClose}
        />
      )}

      <SendMessagePopup
        open={!!sendMessagePopupRecord}
        record={sendMessagePopupRecord}
        onClose={() => setSendMessagePopupRecord(null)}
      />

      <ModalBatteryHistory
        open={batteryModalVisible}
        onClose={() => setBatteryModalVisible(false)}
        device={selectedDevice}
      />

      <ModalLocationInfo
        visible={locationModalVisible}
        onClose={() => setLocationModalVisible(false)}
        device={selectedDeviceLocation}
      />

        <ImportDevicesModal
            open={importModalVisible}
            loading={importLoading}
            onClose={() => setImportModalVisible(false)}
            onImport={async (args) => {
                try {
                    setImportLoading(true);

                    // Accept both shapes:
                    // - new: { bundle: { dsync, clients, events, deviceConfigs, devices } }
                    // - legacy: { devices: [...] }
                    const payload =
                        args?.bundle
                            ? args.bundle
                            : Array.isArray(args?.devices)
                                ? { devices: args.devices }
                                : {};

                    // Post to unified importer (backend normalizes with normalizePayload)
                    const { data } = await axios.post('/devices/import', payload);

                    // Compute a friendly count for the toast
                    const devicesInPayload = Array.isArray(payload.devices)
                        ? payload.devices.length
                        : Array.isArray(args?.bundle?.devices)
                            ? args.bundle.devices.length
                            : (args?.bundle?.dsync?.count ?? 0);

                    // Prefer backend summary if present
                    const summary = data?.summary;
                    const msg = summary
                        ? `Import completed. Devices: ${summary.devicesUpserted ?? devicesInPayload}, Clients: ${summary.clientsUpserted ?? 0}, Events: ${summary.eventsUpserted ?? 0}, Configs: ${summary.deviceConfigsUpserted ?? 0}.`
                        : `Import completed (${devicesInPayload} devices).`;

                    message.success(msg);

                    // Refresh list
                    const refreshed = await axios.get('/devices/admin');
                    setDevices(refreshed.data);

                    setImportModalVisible(false);
                } catch (err) {
                    console.error(err);
                    message.error('Failure to import.');
                } finally {
                    setImportLoading(false);
                }
            }}
        />

        <UpdateBulkClient
            open={bulkClientModalVisible}
            loading={bulkClientLoading}
            clients={clients}
            selectedRowKeys={selectedRowKeys}
            bulkClientId={bulkClientId}
            setBulkClientId={setBulkClientId}
            onCancel={() => {
                setBulkClientModalVisible(false);
                setBulkClientId(undefined);
            }}
            onOk={handleBulkChangeClient}
        />

    </div>
  );
};

export default DeviceListAdmin;
